import { useMutation } from '@tanstack/react-query'
import { monitorException } from '@BUP/services/monitor'
import { enrichError } from '@BUP/services/monitor/enrichError'

/**
 * Shared error-monitoring wrapper for the course-assignment mutation hooks, which otherwise each
 * hand-roll the identical `onError` boilerplate - a code review found this had already silently
 * diverged (some hooks reported every error, others suppressed 400s, with no single place
 * documenting which is correct for which endpoint). `shouldReportError` makes that choice explicit
 * per hook instead of copy-pasted: default is "always report" (most of these hooks' endpoints take
 * already client-validated input, so a 400 reaching here is itself the bug worth seeing); a hook
 * whose 400s are expected user-input mistakes already shown inline (e.g. a CSV upload's row
 * validation errors) opts out by passing its own predicate.
 * @param {Object} params
 * @param {Function} params.mutationFn
 * @param {string} params.hookName - for the Sentry tag, so a report is traceable to its hook
 * @param {string} params.errorMessage
 * @param {(error: Error) => boolean} [params.shouldReportError]
 * @param {Function} [params.onSuccess]
 */
export const useCourseAssignmentMutation = ({
  mutationFn,
  hookName,
  errorMessage,
  shouldReportError = () => true,
  onSuccess
}) => {
  return useMutation({
    mutationFn,
    onSuccess,
    onError: (error) => {
      if (!shouldReportError(error)) return
      const wrappedError = new Error(errorMessage, { cause: error })
      monitorException(wrappedError, {
        tags: { hook: hookName },
        extra: enrichError(error)
      })
    }
  })
}
